import { getInitialRoute } from "../utilities/AppBootstrapRouteUtils";
import { getActionCreatorProviders, getAgentProviders, getAsyncDispatcher, getAuthServiceProvider, getContextAndSetMomentCulture, getLogicModuleProviders, getPersistentAsyncStorage, getRequestPrioritizer, getServiceProviders, getStoreProviders, getTelemetryContext, loadAndInitializeWorkers, logPlanViewSubRouteType, setupAppContext, } from "../utilities/AppBootstrapUtils";
// Constants
import { ClientFlavor, Environment } from "@ms/uno-constants/lib/local/AppConstants";
import { EngagementTelemetryConstants } from "@ms/uno-constants/lib/local/EngagementTelemetryConstants";
import { LoggingStrings } from "@ms/uno-constants/lib/local/LoggingConstants";
import { AppContext } from "@ms/uno-constants/lib/local/configuration/AppContext";
// Controls
import { applyUnoGlobalStyleOverrides } from "@ms/uno-shell/lib/local/controls/unoShell/Global.styles";
import { AgentFactory } from "../base/factories/AgentFactory";
import { ProductionServiceOrchestrator } from "../base/factories/ProductionServiceOrchestrator";
import { MetaOSAppLogicModuleFactory } from "../metaOS/factories/MetaOSAppLogicModuleFactory";
import { MetaOSAppServiceFactory } from "../metaOS/factories/MetaOSAppServiceFactory";
// Flux
import { DynamicImportFailureAction } from "@ms/uno-actions/lib/local/app/DynamicImportFailureAction";
import { FluxRegistry } from "@ms/uno-fluxcore/lib/local/registry";
import { UnoRegistryDescriptor } from "../UnoRegistryDescriptor";
// HostAdaptors
import { ExpectedFailureReason, FailedReason } from "@ms/uno-hostadaptors/lib/local/IHostAdaptor";
import { MetaOSAppWorkerLoader } from "../metaOS/loaders/MetaOSAppWorkerLoader";
import { InitializationError } from "@ms/uno-errors/lib/local/errors/InitializationError";
import { LoggerManager } from "@ms/uno-telemetry/lib/local/LoggerManager";
import { TraceLevel } from "@ms/uno-telemetry/lib/local/events/Trace.event";
import { initializeEngagementLogger, setCurrentRouteInfoForLogs } from "@ms/uno-telemetry/lib/local/utilities/LogUtilities";
import { logNotificationInfo } from "@ms/uno-routing";
import { PageTimingKey, setPageTiming } from "@ms/uno-utilities/lib/local";
import { getUserAliasFromUpn } from "@ms/uno-utilities/lib/local/StringUtilities";
/**
 * Configures the MetaOS host adaptor by initializing the SDK, setting up application context,
 * configuring loggers, and determining the initial route for the application.
 *
 * This function ensures that the host adaptor is initialized only once, sets up telemetry and privacy contexts,
 * and handles special cases such as background load disabling and nested app authentication.
 * It also logs important bootstrap information and validates configuration consistency.
 *
 * @param hostAdaptor - The MetaOS host adaptor instance to be configured.
 * @param performanceMarker - The performance marker used for logging performance metrics.
 * @returns A promise that resolves to an object containing:
 *   - `loggers`: The logger context for the application.
 *   - `configProvider`: The configuration provider function.
 *   - `context`: The application context.
 *   - `initialRoute`: The initial route descriptor for the application.
 *   - `initialRouteFromDeepLink`: The initial route descriptor if provided by a deep link, or null.
 *   - `telemetryLoggers`: The telemetry logger context.
 * @throws {InitializationError} If initialization fails or background load is disabled by a change gate.
 */
export async function configureHostAdaptor(hostAdaptor, performanceMarker) {
    // Teams SDK, AppContext needs to be initialized only once. Re-boot for cached load should skip the same.
    if (!hostAdaptor.isInitialized()) {
        await hostAdaptor.initializeSdk();
        try {
            setupAppContext();
        }
        catch (e) {
            const err = new InitializationError(e.name, e.message);
            hostAdaptor.notifyRenderFailure({ reason: FailedReason.Other, message: err.message });
            // explicitly throw error here (as it's not sufficiently handled)
            throw err;
        }
    }
    const configProvider = () => AppContext.appConfiguration;
    setPageTiming(PageTimingKey.buildId, configProvider().sessionMetaData?.clientBuild);
    const clientFlavor = configProvider().sessionMetaData.appMetadata.clientFlavor;
    hostAdaptor.setConfigProvider(configProvider);
    const context = await getContextAndSetMomentCulture(hostAdaptor);
    const telemetryContext = getTelemetryContext(context);
    const privacyGuardContext = {
        userName: context.displayName ?? undefined,
        userAlias: context.userUpn ? getUserAliasFromUpn(context.userUpn) : undefined,
    };
    /** The permanent Ring the user belongs to in ECS */
    const homeRingId = configProvider().sessionMetaData?.homeRingId ?? "";
    // Setup loggers
    const loggerManager = new LoggerManager(telemetryContext, privacyGuardContext, configProvider, LoggingStrings.ProductName);
    const telemetryLoggers = loggerManager.getLoggers();
    const loggers = {
        ...telemetryLoggers,
        performanceMarker,
    };
    hostAdaptor.setLoggers(loggers);
    // Early exit on background load if the change gate is enabled.
    // This should be used only in case of an incident and not for feature flighting,
    // as failure to load would mark a bad background load history for the Planner app in Teams.
    // Primarily use Teams app manifest backgroundLoadConfiguration property for enabling background load (pre-warming/pre-fetching).
    // NotifyExpectedFailure would indicate failure to the host but would convey that it is expected and not a reliability impact for the end user.
    if (configProvider().settings.disableTeamsAppBackgroundLoad && context.isBackgroundLoad) {
        hostAdaptor.notifyExpectedFailure({
            reason: ExpectedFailureReason.Other,
            message: `[Bootstrap] Background load disabled by change gate`,
        });
        return Promise.reject(new InitializationError("BackgroundLoadDisabled", "Background load is disabled by change gate"));
    }
    // Nested app auth is the only supported token acquisition path, so a setup failure is fatal.
    try {
        await hostAdaptor.setupNestedAppAuth(loggers);
    }
    catch (error) {
        loggers.traceLogger.logTrace(0x1d895246 /* tag_38vjg */, TraceLevel.Error, `[ErrorType=SetupNestedAppAuth][errorCode=${error?.errorCode}][subError=${error?.subError}]`);
        hostAdaptor.notifyRenderFailure({
            reason: FailedReason.AuthFailed,
            message: `[Bootstrap] Nested app auth setup failed`,
        });
        throw error;
    }
    const environment = configProvider().sessionMetaData.environment;
    let initialRoute = getInitialRoute(clientFlavor, environment);
    const initialRouteFromDeepLink = (await hostAdaptor.getInitialRoute()) ?? null;
    if (initialRouteFromDeepLink != null) {
        initialRoute = initialRouteFromDeepLink;
    }
    setCurrentRouteInfoForLogs(initialRoute.subRouteType, initialRoute.subRoute, loggers.logHandler);
    const contextLoggingParams = context.extraLoggingInfo === undefined ? {} : context.extraLoggingInfo();
    // Logging XSessionId(Current teams sessionId) to correlate
    const { hostname } = location;
    loggers.traceLogger.logTrace(0x1e184822 /* tag_4ge68 */, TraceLevel.Info, `[Bootstrap] Microsoft Teams SDK is initialized and bootstrapping application`, {
        HomeRingId: homeRingId,
        CorrelationId: configProvider().sessionMetaData.appLoadCorrelationId,
        ServiceBuildVersion: configProvider().sessionMetaData.serverBuild,
        Hostname: hostname,
        isBackgroundLoad: String(context.isBackgroundLoad),
        ...contextLoggingParams,
    });
    const ringIdValidationErrorMessage = hostAdaptor.validateRingId();
    if (ringIdValidationErrorMessage) {
        const err = new InitializationError("InvalidRingId", ringIdValidationErrorMessage);
        loggers.traceLogger.logTrace(0x1e184821 /* tag_4ge67 */, TraceLevel.Error, `[ErrorType=${err.name}][Error=${err.message}]`);
        // TODO WI #1234: Evaluate whether to stop here, notifyFailure or re-throw upon config mismatch with host, based on telemetry/testing
    }
    // Check if the combination of rollout stage and environment is unexpected
    const isCombinationUnexpected = AppContext.isRolloutStageAndEnvironmentCombinationUnexpected();
    if (isCombinationUnexpected) {
        loggers.traceLogger.logTrace(0x1e184820 /* tag_4ge66 */, TraceLevel.Error, `[Bootstrap] Found invalid combination of rollout stage and environment: [RolloutStage=${configProvider().settings.rolloutStage}][Environment=${environment}]`);
    }
    return {
        loggers: loggers,
        configProvider: configProvider,
        context: context,
        initialRoute: initialRoute,
        initialRouteFromDeepLink: initialRouteFromDeepLink,
        telemetryLoggers: telemetryLoggers,
    };
}
/**
 * Initializes and sets up all core providers required for bootstrapping a MetaOS application.
 *
 * This function configures and returns agent, logic module, service, store providers, as well as
 * the dispatcher and request prioritizer. It also sets up engagement logging, authentication modules,
 * and service orchestration.
 *
 * @param configProvider - Provides configuration and session metadata for the app.
 * @param context - The application context, including extra logging information.
 * @param engagementConstantName - The constant name used for engagement telemetry.
 * @param initialRoute - The initial route descriptor for the app.
 * @param hostAdaptor - The host adaptor for MetaOS integration.
 * @param loader - The component loader for dynamic imports.
 * @param loggers - The logging context for engagement and diagnostic logging.
 * @param storeFactory - Factory for creating app store providers.
 * @returns An object containing initialized agent providers, dispatcher, logic module providers,
 *          request prioritizer, service providers, and store providers.
 *
 * @remarks
 * - Engagement logging is initialized and relevant telemetry is logged.
 * - Authentication modules are set up early to support service initialization.
 * - The dispatcher is configured for the Flux system.
 * - Service orchestration is established with proper authentication and background state awareness.
 * - Providers are returned for use in application bootstrap.
 */
export async function setupProviders(configProvider, context, engagementConstantName, initialRoute, hostAdaptor, loader, loggers, storeFactory) {
    const storeProviders = getStoreProviders(storeFactory);
    // Initialize agent providers
    const agentFactory = new AgentFactory(loader);
    const agentProviders = getAgentProviders(agentFactory);
    // Initialize engagement logging
    initializeEngagementLogger(storeProviders.route().getCurrentSubRouteType.bind(storeProviders.route()), loggers.engagementLogger, LoggingStrings.ProductName);
    /** The permanent Ring the user belongs to in ECS */
    const homeRingId = configProvider().sessionMetaData?.homeRingId ?? "";
    const { hostname } = location;
    const contextLoggingParams = context.extraLoggingInfo === undefined ? {} : context.extraLoggingInfo();
    // Log relevant engagement info (which can only be done AFTER we've initialized EngagementLogger)
    loggers.engagementLogger.logEngagement(engagementConstantName, EngagementTelemetryConstants.Bootstrap, {
        HomeRingId: homeRingId,
        Hostname: hostname,
        ...contextLoggingParams,
    });
    const clientFlavor = configProvider().sessionMetaData.appMetadata.clientFlavor;
    if (clientFlavor !== ClientFlavor.TaskFromMessage) {
        logNotificationInfo(initialRoute, loggers);
        logPlanViewSubRouteType(engagementConstantName, storeProviders.route().getCurrentSubRouteType(), loggers);
    }
    const registry = new FluxRegistry(UnoRegistryDescriptor);
    // Dispatcher for the flux system.
    const dispatcher = getAsyncDispatcher(configProvider, registry, storeFactory, loggers);
    loader.setOnFailure((name) => dispatcher.dispatchActionAsync(new DynamicImportFailureAction()));
    // Set up request prioritizer
    const requestPrioritizer = getRequestPrioritizer(storeProviders, loggers);
    // Setup Providers
    const serviceFactory = new MetaOSAppServiceFactory(loader, loggers);
    // lazy loaded logic modules depend on lazy loaded services typically but authModule as an exception is needed by services upfront.
    // That is why authModule is created here and passed to services.
    const authService = getAuthServiceProvider(serviceFactory, dispatcher, hostAdaptor, loggers, configProvider)();
    const authStore = storeFactory.getAuthStoreProvider()();
    const lmFactory = new MetaOSAppLogicModuleFactory(loader, loggers);
    const authModule = lmFactory.getAuthModuleProvider(dispatcher, authService, authStore, configProvider)();
    // Auth module's fetchAccessTokenAsync method needed by services
    const fetchAccessToken = authModule.fetchAccessTokenAsync.bind(authModule);
    // Auth module's refreshAccessTokenAsync method needed by services
    const refreshAccessToken = authModule.refreshAccessTokenAsync.bind(authModule);
    // Function to check if the app is in background. This is used by Planner service to set the appropriate usage headers.
    const isAppInBackground = () => {
        const appContextStore = storeFactory.getAppContextStoreProvider()();
        return appContextStore.isAppInBackground();
    };
    const serviceOrchestrator = new ProductionServiceOrchestrator(serviceFactory, requestPrioritizer, configProvider, loggers, fetchAccessToken, refreshAccessToken, isAppInBackground);
    const serviceProviders = getServiceProviders(serviceOrchestrator, authService);
    const lmProviders = getLogicModuleProviders(lmFactory, dispatcher, serviceProviders, storeProviders, configProvider, hostAdaptor, authService, authStore, fetchAccessToken);
    return { agentProviders, dispatcher, lmProviders, requestPrioritizer, serviceProviders, storeProviders };
}
/**
 * Initializes and sets up the worker loader, UnoShell properties, and persistent async storage for the MetaOS application.
 *
 * This function prepares the core dependencies and configuration required to bootstrap the application,
 * including action creator providers, worker loader, UnoShell props, and persistent storage.
 * It also triggers the application start action for performance collection.
 *
 * @param agentProviders - Providers for agent-related services and dependencies.
 * @param context - The application context containing user and session information.
 * @param configProvider - Function to retrieve the current configuration.
 * @param dispatcher - Asynchronous dispatcher for event management.
 * @param hostAdaptor - Host adaptor for communication with the host environment.
 * @param loader - Component loader for dynamic module loading.
 * @param loggers - Logger context for application logging.
 * @param lmProviders - Providers for logic modules.
 * @param actionCreatorFactory - Factory for creating action creators.
 * @param storeProviders - Providers for application stores.
 * @param storeFactory - Factory for creating and hydrating stores.
 * @param serviceProviders - Providers for application services.
 * @param telemetryLoggers - Logger context for telemetry and analytics.
 * @returns A promise that resolves to an object containing:
 *   - `acProviders`: The action creator providers.
 *   - `unoShellProps`: The UnoShell properties for application shell setup.
 *   - `workerLoader`: The initialized worker loader instance.
 *   - `persistentAsyncStorage`: The persistent async storage instance or null if not available.
 */
export async function setupWorkerLoaderAndUnoShellProps(agentProviders, context, configProvider, dispatcher, hostAdaptor, loader, loggers, lmProviders, actionCreatorFactory, storeProviders, storeFactory, serviceProviders, telemetryLoggers) {
    const acProviders = getActionCreatorProviders(actionCreatorFactory);
    // Fire app start action (must be done after store init)
    const perfCollectionActionCreator = acProviders.performanceCollection();
    perfCollectionActionCreator.appStart(storeProviders.route().getCurrentPath(), Date.now());
    // Setup persistent async storage
    const persistentAsyncStorage = getPersistentAsyncStorage(configProvider, context.userId, loggers);
    const workerLoader = new MetaOSAppWorkerLoader(acProviders, storeProviders, (store) => storeFactory.loadForPersistence(store), (store) => storeFactory.loadForHydration(store), loader, loggers, telemetryLoggers, null, persistentAsyncStorage, configProvider, serviceProviders, lmProviders, hostAdaptor, dispatcher);
    const showDevTools = configProvider().sessionMetaData.environment === Environment.Ppe;
    const unoShellProps = {
        dispatcherEventManagement: dispatcher,
        actionCreatorProviders: acProviders,
        logicModuleProviders: lmProviders,
        serviceProviders: serviceProviders,
        storeProviders: storeProviders,
        workerLoader: workerLoader,
        hostAdaptor: hostAdaptor,
        loggers: loggers,
        onLoad: () => hostAdaptor.notifyLoaded(),
        onSuccess: () => {
            if (!configProvider().flights.EnableTeamsNotifySuccessV2) {
                hostAdaptor.notifyRenderSuccess();
            }
        },
        onFail: (message) => hostAdaptor.notifyRenderFailure({ reason: FailedReason.Other, message }),
        configProvider: configProvider,
        showDevTools: showDevTools,
        agentProviders: agentProviders,
    };
    return { acProviders, unoShellProps, workerLoader, persistentAsyncStorage };
}
/**
 * Loads and initializes workers, applies global style overrides, registers theme change handlers,
 * and creates a default policy based on the provided configuration and context.
 *
 * @param acProviders - The action creator providers used for registering theme changes.
 * @param configProvider - A function that provides the current configuration object.
 * @param context - The application context, including information such as background load state.
 * @param hostAdaptor - The host adaptor used for theme registration and other host-specific operations.
 * @param loggers - The logger context for logging operations during worker initialization.
 * @param persistentAsyncStorage - Optional persistent asynchronous storage for planner schema data.
 * @param requestPrioritizer - The request prioritizer used when loading and initializing workers.
 * @param workerLoader - The worker loader responsible for loading and initializing workers.
 *
 * @remarks
 * - Workers are only loaded and initialized if deferred boot for background load is disabled or if the load is not a background load.
 * - Global style overrides are applied before any classes or styles are generated.
 * - Theme change handlers are only registered if the app is not in background load mode.
 * - A default policy is created if the corresponding flight is enabled in the configuration.
 */
export function loadWorkersAndRegisterTheme(acProviders, configProvider, context, hostAdaptor, loggers, persistentAsyncStorage, requestPrioritizer, workerLoader) {
    if (configProvider().changeGates.DisableDeferredBootForBackgroundLoad || !context.isBackgroundLoad) {
        loadAndInitializeWorkers(workerLoader, requestPrioritizer, loggers, persistentAsyncStorage, configProvider, hostAdaptor);
    }
    // Apply Global styles before any classes or styles are generated.
    applyUnoGlobalStyleOverrides();
    // Do not register theme change handler for background load. It will be registered when the app is loaded in foreground.
    // If `isBackgroundLoad` is not present, it defaults to false.
    if (!context.isBackgroundLoad) {
        registerThemeChange(hostAdaptor, acProviders);
    }
}
/**
 * Register theme change handler to update theme
 * @param hostAdaptor The host adaptor
 * @param acProviders The action creator providers
 */
export function registerThemeChange(hostAdaptor, acProviders) {
    // Register theme change handler to update theme
    hostAdaptor.registerThemeChangeHandler(async (theme) => {
        const appContextAc = await acProviders.appContext();
        appContextAc.updateAppTheme(theme);
    });
}
